import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { startLive } from '../realtime/live';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { Button, Icon, Modal, useToast } from '../ui/kit';
import { HotelLogo } from '../ui/HotelLogo';
import { OfflineBar, useOutbox } from '../offline/OfflineBar';
import { outbox } from '../offline/outbox';
import { ChangePassword } from '../pages/ChangePassword';

const ROLE_LABEL: Record<string, string> = {
  OWNER: 'Owner',
  MANAGER: 'Manager',
  FRONT_DESK: 'Front desk',
  ACCOUNTANT: 'Accountant',
  HOUSEKEEPING: 'Housekeeping',
  RESTAURANT: 'Restaurant',
  KITCHEN: 'Kitchen',
  DRIVER: 'Driver',
  ATTENDANT: 'Pool attendant',
};

interface Item {
  to: string;
  label: string;
  icon: string;
  show: boolean;
  end?: boolean;
  /** candidates for the phone's bottom bar; everything else goes under "More" */
  primary: boolean;
}

const MAX_BOTTOM = 4;

export function Shell() {
  const { me, can, logout } = useAuth();
  const [moreOpen, setMoreOpen] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const loc = useLocation();
  const toast = useToast();
  const pending = useOutbox().waiting.length;
  const qc = useQueryClient();
  useEffect(() => startLive(qc), [qc]); // live updates while signed in; screens poll slowly while it is connected
  useEffect(() => {
    outbox.onQueued = (label) => toast.ok(`Saved on this device: ${label}. It will be sent when the connection returns.`);
    return () => {
      outbox.onQueued = null;
    };
  }, [toast]);
  if (!me) return null;

  const all: Item[] = [
    { to: '/', label: 'Today', icon: 'today', show: can('booking.view'), end: true, primary: true },
    { to: '/bookings', label: 'Bookings', icon: 'bookings', show: can('booking.view'), primary: true },
    { to: '/new', label: 'New', icon: 'plus', show: can('booking.create'), primary: true },
    { to: '/rooms', label: 'Rooms', icon: 'rooms', show: can('inventory.view'), primary: true },
    { to: '/restaurant', label: 'Restaurant', icon: 'utensils', show: can('order.create'), primary: true },
    { to: '/bar', label: 'Bar sale', icon: 'utensils', show: can('order.create'), primary: false },
    { to: '/kitchen', label: 'Kitchen', icon: 'flame', show: can('kitchen.view'), primary: true },
    { to: '/till', label: 'Till', icon: 'till', show: can('shift.manage'), primary: true },
    { to: '/menu', label: 'Menu and tables', icon: 'book', show: can('menu.manage'), primary: false },
    { to: '/stock', label: 'Inventory', icon: 'inbox', show: can('stock.view'), primary: false },
    { to: '/pool', label: 'Pool', icon: 'wave', show: can('pool.view'), primary: true },
    { to: '/transport', label: 'Transport', icon: 'car', show: can('transport.view'), primary: false },
    { to: '/trips', label: 'My trips', icon: 'car', show: can('transport.drive'), primary: true },
    { to: '/housekeeping', label: 'Housekeeping', icon: 'broom', show: can('housekeeping.view'), primary: true },
    { to: '/laundry', label: 'Laundry', icon: 'broom', show: can('laundry.view'), primary: false },
    { to: '/messages', label: 'Messages', icon: 'inbox', show: can('message.view'), primary: false },
    { to: '/channels', label: 'Channels', icon: 'bookings', show: can('channel.view'), primary: false },
    { to: '/guests', label: 'Guests', icon: 'people', show: can('guest.manage'), primary: false },
    { to: '/roster', label: 'Roster', icon: 'bookings', show: can('roster.view'), primary: false },
    { to: '/people', label: 'People', icon: 'people', show: can('hr.view'), primary: false },
    { to: '/payroll', label: 'Payroll', icon: 'wallet', show: can('payroll.view'), primary: false },
    { to: '/my-pay', label: 'My pay', icon: 'wallet', show: can('self.service'), primary: false },
    { to: '/summary', label: 'Daily summary', icon: 'chart', show: can('reports.view'), primary: false },
    { to: '/insights', label: 'Insights', icon: 'chart', show: can('reports.view'), primary: false },
    { to: '/reports', label: 'Reports', icon: 'chart', show: can('ledger.view'), primary: false },
    { to: '/accounts', label: 'Accounts', icon: 'books', show: can('ledger.view'), primary: false },
    { to: '/night-audit', label: 'Night audit', icon: 'moon', show: can('ledger.view'), primary: false },
    { to: '/setup', label: 'Setup', icon: 'setup', show: can('inventory.manage') || can('rates.manage') || can('property.manage'), primary: false },
    { to: '/staff', label: 'Staff logins', icon: 'people', show: can('staff.manage'), primary: false },
    { to: '/audit', label: 'Audit log', icon: 'shield', show: can('audit.view'), primary: false },
  ];
  const items = all.filter((i) => i.show);
  // the bottom bar keeps at most four destinations; the rest are one tap away under "More"
  const bottom = items.filter((i) => i.primary).slice(0, MAX_BOTTOM);
  const rest = items.filter((i) => !bottom.includes(i));
  const moreActive = rest.some((i) => loc.pathname.startsWith(i.to));

  const brand = (
    <>
      <HotelLogo version={me.property.logoVersion} />
      <span>
        {me.property.name}
        <small>{ROLE_LABEL[me.role] ?? me.role}</small>
      </span>
    </>
  );

  return (
    <div className="shell">
      <header className="topbar">
        <div className="brandmark">{brand}</div>
        <div className="spacer" />
        <span className="small muted">{me.name}</span>
        <Button variant="ghost" onClick={() => setChangingPassword(true)} aria-label="Change my password">
          <Icon name="shield" />
          <span className="sr-only">Change my password</span>
        </Button>
        <Button variant="ghost" onClick={() => (pending > 0 ? setLeaving(true) : void logout())} aria-label="Sign out">
          <Icon name="out" />
          <span className="sr-only">Sign out</span>
        </Button>
      </header>

      <nav className="nav" aria-label="Main">
        <div className="nav-brand brandmark">{brand}</div>
        {items.map((i) => (
          <NavLink key={i.to} to={i.to} end={i.end ?? false} className={bottom.includes(i) ? '' : 'secondary'}>
            <Icon name={i.icon} />
            <span>{i.label}</span>
          </NavLink>
        ))}
        {rest.length ? (
          <button type="button" className="more" aria-current={moreActive ? 'page' : undefined} onClick={() => setMoreOpen(true)}>
            <Icon name="more" />
            <span>More</span>
          </button>
        ) : null}
      </nav>

      <Modal open={moreOpen} onClose={() => setMoreOpen(false)} title="More">
        <div className="stack">
          {rest.map((i) => (
            <NavLink key={i.to} to={i.to} className="btn block" onClick={() => setMoreOpen(false)}>
              <Icon name={i.icon} />
              {i.label}
            </NavLink>
          ))}
        </div>
      </Modal>

      {changingPassword ? <ChangePassword onClose={() => setChangingPassword(false)} /> : null}
      <Modal open={leaving} onClose={() => setLeaving(false)} title="Changes not sent yet">
        <div className="stack">
          <p>
            {pending} change{pending === 1 ? ' has' : 's have'} not reached the server yet. If you sign out now {pending === 1 ? 'it stays' : 'they stay'} on this device and {pending === 1 ? 'is' : 'are'} sent the next time you sign in here with the connection on.
          </p>
          <div className="modal-foot">
            <Button onClick={() => setLeaving(false)}>Stay signed in</Button>
            <Button
              variant="danger"
              onClick={() => {
                setLeaving(false);
                void logout();
              }}
            >
              Sign out anyway
            </Button>
          </div>
        </div>
      </Modal>

      <main>
        <OfflineBar />
        <Outlet />
      </main>
    </div>
  );
}
